import { useMutation, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { historyService } from '../api/history.service';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import { conceptsService } from '@/shared/api/services/concepts.service';
import { projectsService } from '@/features/project';
import { toast } from '@/shared/lib/toast';
import { ApiClientError } from '@/shared/lib/api';
import type {
  TrashItem,
  TrashResponse,
  TrashSubtypeFilter,
} from '../model/trash.types';
import { isTrashProject, trashItemProjectId } from '../model/trash.types';

/**
 * Paginated Trash feed. The API already drops anything past the restore
 * deadline, so there is nothing to filter here.
 */
export function useInfiniteTrash(subtype: TrashSubtypeFilter = 'all', pageSize = 20) {
  return useInfiniteQuery<TrashResponse>({
    queryKey: cacheKeys.trash.infinite(subtype),
    queryFn: ({ pageParam = 1 }) =>
      historyService.getTrash({
        subtype,
        page: { number: pageParam as number, size: pageSize },
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.meta.current_page < lastPage.meta.last_page
        ? lastPage.meta.current_page + 1
        : undefined,
    // A row can lapse out of the window while the tab sits open, so never serve
    // this feed from a stale cache: the promise on screen must match the one
    // the restore endpoint will honour.
    staleTime: 0,
  });
}

/**
 * Restore one row.
 *
 * Both outcomes are the server's words: the refusal past the window arrives as
 * a 422 whose detail is already localized, and it is shown as is.
 */
export function useRestoreTrashItem() {
  const queryClient = useQueryClient();
  const t = useTranslations('history.trash');

  return useMutation({
    mutationKey: ['trash', 'restore'],
    mutationFn: async (item: TrashItem) => {
      const projectId = trashItemProjectId(item);
      if (!projectId) {
        throw new Error('Trash item has no building to restore into');
      }

      if (isTrashProject(item)) {
        await projectsService.restore(projectId);
        return;
      }

      await conceptsService.restoreConcept(projectId, item.id);
    },
    onSuccess: (_result, item) => {
      toast.success(
        isTrashProject(item) ? t('buildingRestored') : t('designRestored')
      );
      queryClient.invalidateQueries({ queryKey: cacheKeys.trash.all });
      queryClient.invalidateQueries({ queryKey: cacheKeys.projects.all });
      queryClient.invalidateQueries({ queryKey: cacheKeys.jobHistory.all });
      queryClient.invalidateQueries({ queryKey: cacheKeys.history.all });
    },
    onError: (error) => {
      // Displayed verbatim: the window refusal, the project-limit refusal and
      // the deleted-building refusal are all composed by the API.
      toast.error(
        error instanceof ApiClientError || error instanceof Error
          ? error.message
          : t('restoreFailed')
      );
      // The row may have lapsed or the state moved on — re-read the feed so the
      // list stops offering what the server just refused.
      queryClient.invalidateQueries({ queryKey: cacheKeys.trash.all });
    },
  });
}
